import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MetricCard, MetricCardsGrid } from "../shared";
import type { OverviewKpiMetric } from "./overviewKpiMappers";

const KPI_TITLE_KEYS = [
  "analytics.overview.kpi.serviceRevenue",
  "analytics.overview.kpi.conversionOfOffers",
  "analytics.overview.kpi.avgGuestRatingForStay",
  "analytics.overview.kpi.ordersForStay",
] as const;

const KPI_TITLE_FALLBACKS = [
  "Service revenue",
  "Conversion of offers",
  "Avg. Guest Rating for Stay",
  "Orders for stay",
] as const;

const LOADING_VALUES = ["0€", "0%", "0.00", "0 services"] as const;

const LOADING_METRICS: OverviewKpiMetric[] = KPI_TITLE_KEYS.map(
  (key, index) => ({
    title: KPI_TITLE_FALLBACKS[index],
    value: LOADING_VALUES[index],
  }),
);

interface OverviewKpiCardsProps {
  metrics?: OverviewKpiMetric[];
  isLoading?: boolean;
  /** Hides delta only while the previous-period comparison is loading. */
  isComparisonLoading?: boolean;
}

export const OverviewKpiCards: React.FC<OverviewKpiCardsProps> = ({
  metrics = LOADING_METRICS,
  isLoading = false,
  isComparisonLoading = false,
}) => {
  const { t } = useTranslation();

  const loadingMetrics = useMemo<OverviewKpiMetric[]>(
    () =>
      KPI_TITLE_KEYS.map((key, index) => ({
        title: t(key, KPI_TITLE_FALLBACKS[index]),
        value: LOADING_VALUES[index],
      })),
    [t],
  );

  const displayMetrics = isLoading ? loadingMetrics : metrics;

  return (
    <MetricCardsGrid columns={4}>
      {displayMetrics.map((metric, index) => (
        <MetricCard
          key={KPI_TITLE_KEYS[index]}
          title={t(KPI_TITLE_KEYS[index], metric.title)}
          value={metric.value}
          delta={isComparisonLoading ? undefined : metric.delta}
          trend={metric.trend}
          width="fill"
        />
      ))}
    </MetricCardsGrid>
  );
};
